/**
 * Login page. Uses design system components (Card, Input, Button).
 * Calls authService.requestToken, then saves the token via functions.ts before
 * invoking onLogin so the rest of the app proceeds authenticated.
 */

import { createButton, createCard, createInput } from "@aiapollo/design-system"
import { getBrandConfig, type WhitelabelBrandConfig } from "../../../skins"
import { requestToken } from "../../services/authService"
import { getApiBaseUrl, saveAccessToken, saveUserName } from "../../../functions/functions"

export type LoginPageOptions = {
	/** Called after a successful login. The token is already stored before this fires. */
	onLogin: () => void
	/**
	 * Brand preset (copy, logos). Defaults to {@link getBrandConfig} from `?brand=` on the task pane URL.
	 */
	brand?: WhitelabelBrandConfig
}

export function createLoginPage({ onLogin, brand }: LoginPageOptions): HTMLElement {
	const b = brand ?? getBrandConfig()
	const root = document.createElement("div")
	root.className = "login-page"
	root.setAttribute("role", "main")

	// ── Branding ─────────────────────────────────────────────────────────────
	const logoWrap = document.createElement("div")
	logoWrap.className = "login-page__logo"
	const logoImg = document.createElement("img")
	logoImg.src = b.logoUrl
	logoImg.width = 56
	logoImg.height = 56
	logoImg.alt = b.logoAlt
	const appName = document.createElement("p")
	appName.className = "login-page__app-name"
	appName.textContent = b.displayName
	logoWrap.appendChild(logoImg)
	logoWrap.appendChild(appName)

	// ── Form card ─────────────────────────────────────────────────────────────
	const card = createCard({ title: b.signInCardTitle, padding: "md" })

	const formContent = document.createElement("div")
	formContent.className = "card__content login-page__form"

	// Username
	const usernameWrap = createInput({ label: "Username", placeholder: "username" })
	const usernameInput = usernameWrap.querySelector("input") as HTMLInputElement
	usernameInput.autocomplete = "username"

	// Password
	const passwordWrap = createInput({
		label: "Password",
		placeholder: "password",
		type: "password",
	})
	const passwordInput = passwordWrap.querySelector("input") as HTMLInputElement
	passwordInput.autocomplete = "current-password"

	// Error banner (hidden until needed)
	const errorEl = document.createElement("p")
	errorEl.className = "login-page__error"
	errorEl.setAttribute("role", "alert")
	errorEl.setAttribute("aria-live", "assertive")
	errorEl.hidden = true

	// Sign-in button
	const signInBtn = createButton({ label: "Sign in", variant: "primary", size: "md" })

	// ── Submit logic ──────────────────────────────────────────────────────────
	async function attemptLogin(): Promise<void> {
		const username = usernameInput.value.trim()
		const password = passwordInput.value

		if (!username || !password) {
			errorEl.textContent = "Please enter both username and password."
			errorEl.hidden = false
			return
		}

		signInBtn.disabled = true
		const labelSpan = signInBtn.querySelector(".btn__label")
		if (labelSpan) labelSpan.textContent = "Signing in…"
		errorEl.hidden = true

		try {
			const baseUrl = getApiBaseUrl()
			const tokenResponse = await requestToken(baseUrl, username, password)
			saveAccessToken(tokenResponse.access_token)
			saveUserName(username)
			onLogin()
		} catch (err) {
			const raw = err instanceof Error ? err.message : String(err)
			const isCredentialError =
				raw.includes("401") || raw.includes("400") || raw.includes("403")
			errorEl.textContent = isCredentialError
				? "Incorrect username or password. Please try again."
				: `Sign in failed: ${raw}`
			errorEl.hidden = false
			passwordInput.value = ""
			passwordInput.focus()
		} finally {
			signInBtn.disabled = false
			const span = signInBtn.querySelector(".btn__label")
			if (span) span.textContent = "Sign in"
		}
	}

	signInBtn.addEventListener("click", () => void attemptLogin())

	// Enter on username → focus password; Enter on password → submit
	usernameInput.addEventListener("keydown", (e) => {
		if (e.key === "Enter") passwordInput.focus()
	})
	passwordInput.addEventListener("keydown", (e) => {
		if (e.key === "Enter") void attemptLogin()
	})

	// ── Assemble ──────────────────────────────────────────────────────────────
	formContent.appendChild(usernameWrap)
	formContent.appendChild(passwordWrap)
	formContent.appendChild(errorEl)
	formContent.appendChild(signInBtn)
	card.appendChild(formContent)

	root.appendChild(logoWrap)
	root.appendChild(card)

	// Auto-focus username on mount (deferred so element is in DOM)
	requestAnimationFrame(() => usernameInput.focus())

	return root
}
